import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { CatalogItem } from '../types';
import PageHero from '../components/ui/PageHero';
import FilterBar from '../components/ui/FilterBar';
import CatalogCard from '../components/ui/CatalogCard';

interface Category {
  id: string;
  label: string;
}

export default function CatalogPage() {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = useMemo(() => {
    return (t.catalogPage.items as CatalogItem[]).filter(item => {
      const matchesCat = activeCategory === 'all' || item.category === activeCategory;
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.tags.some(tag => tag.toLowerCase().includes(q));
      return matchesCat && matchesSearch;
    });
  }, [search, activeCategory, t]);

  return (
    <>
      <Helmet>
        <title>{t.meta.catalogTitle}</title>
        <meta name="description" content={t.catalogPage.lead} />
      </Helmet>

      <PageHero tag={t.catalogPage.tag} title={t.catalogPage.title} lead={t.catalogPage.lead} />

      <div className="bg-silver-light min-h-screen py-8 md:py-12">
        <div className="container">
          <FilterBar
            search={search}
            onSearch={e => setSearch(e.target.value)}
            searchPlaceholder={t.catalogPage.searchPlaceholder}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            categories={t.catalogPage.categories as Category[]}
            allLabel={t.catalogPage.allCategories}
          />

          {filtered.length === 0 ? (
            <div className="text-center py-16 md:py-20 text-text-mid">
              {t.catalogPage.noResults}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filtered.map((item: CatalogItem, idx: number) => (
                <CatalogCard
                  key={item.id}
                  name={item.name}
                  description={item.description}
                  tags={item.tags}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
